import React, { useEffect, useState } from "react";
import { CalendarDays, Clock, Keyboard, MousePointerClick } from "lucide-react";

// Some people want the wheel picker, some want to just type 14/08/1992.
// Both fields offer either, and hand the parent the same canonical value:
// date as "YYYY-MM-DD", time as "HH:MM" (24h).
const THIS_YEAR = new Date().getFullYear();
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const YEARS = Array.from({ length: THIS_YEAR - 1919 }, (_, i) => THIS_YEAR - i);
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_HI = ["जनवरी", "फ़रवरी", "मार्च", "अप्रैल", "मई", "जून", "जुलाई", "अगस्त", "सितंबर", "अक्टूबर", "नवंबर", "दिसंबर"];
const HOURS12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const labelStyle = { color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" };
const bigInput = { fontSize: "1rem", padding: "13px 14px" };
const selStyle = { fontSize: "0.95rem", padding: "13px 8px", minHeight: "50px" };
const hintStyle = { color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "5px" };
const switchStyle = {
  background: "none", border: "none", cursor: "pointer", padding: "2px 0",
  color: "rgba(255,209,102,0.85)", fontSize: "0.72rem", fontWeight: 600,
  display: "inline-flex", alignItems: "center", gap: "5px", textDecoration: "underline",
  textUnderlineOffset: "3px",
};

const Head = ({ icon, text, mode, onToggle, testid, typeLabel, pickLabel }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "7px", flexWrap: "wrap" }}>
    <label style={labelStyle}>{icon} {text}</label>
    <button type="button" data-testid={testid} onClick={onToggle} style={switchStyle}>
      {mode === "picker" ? <><Keyboard size={12} /> {typeLabel}</> : <><MousePointerClick size={12} /> {pickLabel}</>}
    </button>
  </div>
);

export const DobField = ({ value, onChange, prefix, label = "Date of birth", hi = false }) => {
  const [mode, setMode] = useState("picker");
  const [text, setText] = useState("");
  // Selects keep their own state: a half-filled date can't be pushed upward,
  // but the user must still see what they picked.
  const [parts, setParts] = useState(() => (value
    ? { d: String(+value.slice(8, 10)), m: String(+value.slice(5, 7)), y: value.slice(0, 4) }
    : { d: "", m: "", y: "" }));
  const { d: dPart, m: mPart, y: yPart } = parts;

  const pushParts = (d, m, y) => {
    setParts({ d, m, y });
    if (!d || !m || !y) { onChange(""); return; }
    const dt = new Date(+y, +m - 1, +d);
    if (dt.getDate() !== +d || dt.getMonth() !== +m - 1 || dt > new Date()) { onChange(""); return; }
    onChange(`${y}-${String(+m).padStart(2, "0")}-${String(+d).padStart(2, "0")}`);
  };

  useEffect(() => {
    if (mode !== "type") return;
    if (!value) return;
    const [y, m, d] = value.split("-");
    setText(`${d}/${m}/${y}`);
  }, [mode, value]);

  const onText = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
    if (digits.length === 8) {
      const d = +digits.slice(0, 2), m = +digits.slice(2, 4), y = +digits.slice(4);
      const dt = new Date(y, m - 1, d);
      const valid = d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1920
        && dt.getDate() === d && dt.getMonth() === m - 1 && dt <= new Date();
      onChange(valid ? `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}` : "");
    } else {
      onChange("");
    }
  };

  return (
    <div style={{ marginBottom: "14px" }}>
      <Head icon={<CalendarDays size={13} />} text={label} mode={mode} testid={`${prefix}-date-mode`}
        onToggle={() => setMode(m => (m === "picker" ? "type" : "picker"))}
        typeLabel={hi ? "टाइप करें" : "Type it"} pickLabel={hi ? "चुनें" : "Use dropdowns"} />
      {mode === "picker" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.35fr 1fr", gap: "7px" }}>
          <select data-testid={`${prefix}-date-day`} value={dPart} className="cosmic-input" style={selStyle}
            onChange={(e) => pushParts(e.target.value, mPart, yPart)}>
            <option value="">{hi ? "दिन" : "Day"}</option>
            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid={`${prefix}-date-month`} value={mPart} className="cosmic-input" style={selStyle}
            onChange={(e) => pushParts(dPart, e.target.value, yPart)}>
            <option value="">{hi ? "माह" : "Month"}</option>
            {(hi ? MONTHS_HI : MONTHS_EN).map((mn, i) => <option key={mn} value={i + 1}>{mn}</option>)}
          </select>
          <select data-testid={`${prefix}-date-year`} value={yPart} className="cosmic-input" style={selStyle}
            onChange={(e) => pushParts(dPart, mPart, e.target.value)}>
            <option value="">{hi ? "वर्ष" : "Year"}</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      ) : (
        <input data-testid={`${prefix}-date-text`} type="text" inputMode="numeric" value={text} onChange={onText}
          placeholder={hi ? "दिन/माह/वर्ष — 14/08/1992" : "DD/MM/YYYY — e.g. 14/08/1992"}
          className="cosmic-input" style={bigInput} autoComplete="bday" />
      )}
      <p style={hintStyle}>
        {mode === "picker"
          ? (hi ? "दिन, माह और वर्ष चुनें — या 'टाइप करें' दबाएँ।" : "Pick day, month and year — or tap “Type it” to key it in.")
          : (hi ? "अंक टाइप करें — स्लैश अपने आप लग जाएंगे।" : "Just type the numbers — the slashes appear on their own.")}
      </p>
    </div>
  );
};

export const TobField = ({ value, onChange, prefix, label = "Time of birth", hi = false }) => {
  const [mode, setMode] = useState("picker");
  const [text, setText] = useState("");
  const [period, setPeriod] = useState("PM");
  // Hour / minute / AM-PM dropdowns: no keyboard, no scrubbing a clock face.
  const [h24, min] = value ? value.split(":").map(Number) : [12, 0];
  const hSel = String(h24 % 12 === 0 ? 12 : h24 % 12);
  const mSel = String(min);
  const pSel = h24 >= 12 ? "PM" : "AM";

  const pushSel = (h, m, p) => {
    let hh = +h % 12;
    if (p === "PM") hh += 12;
    onChange(`${String(hh).padStart(2, "0")}:${String(+m).padStart(2, "0")}`);
  };

  useEffect(() => {
    if (mode !== "type" || !value) return;
    const [h, m] = value.split(":").map(Number);
    setPeriod(h >= 12 ? "PM" : "AM");
    setText(`${String(h % 12 === 0 ? 12 : h % 12).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }, [mode, value]);

  const push = (raw, per) => {
    const digits = raw.replace(/\D/g, "");
    if (digits.length < 3) return;
    const h12 = +digits.slice(0, digits.length - 2);
    const mm = +digits.slice(-2);
    if (h12 < 1 || h12 > 12 || mm > 59) return;
    let h24 = h12 % 12;
    if (per === "PM") h24 += 12;
    onChange(`${String(h24).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  };

  const onText = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    const out = digits.length > 2 ? `${digits.slice(0, digits.length - 2)}:${digits.slice(-2)}` : digits;
    setText(out);
    push(out, period);
  };

  return (
    <div style={{ marginBottom: "14px" }}>
      <Head icon={<Clock size={13} />} text={label} mode={mode} testid={`${prefix}-time-mode`}
        onToggle={() => setMode(m => (m === "picker" ? "type" : "picker"))}
        typeLabel={hi ? "टाइप करें" : "Type it"} pickLabel={hi ? "घड़ी" : "Use dropdowns"} />
      {mode === "picker" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "7px" }}>
          <select data-testid={`${prefix}-time-hour`} value={hSel} className="cosmic-input" style={selStyle}
            onChange={(e) => pushSel(e.target.value, mSel, pSel)}>
            {HOURS12.map(h => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
          </select>
          <select data-testid={`${prefix}-time-minute`} value={mSel} className="cosmic-input" style={selStyle}
            onChange={(e) => pushSel(hSel, e.target.value, pSel)}>
            {MINUTES.map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
          </select>
          <select data-testid={`${prefix}-time-ampm`} value={pSel} className="cosmic-input" style={selStyle}
            onChange={(e) => pushSel(hSel, mSel, e.target.value)}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "8px" }}>
          <input data-testid={`${prefix}-time-text`} type="text" inputMode="numeric" value={text} onChange={onText}
            placeholder={hi ? "घंटा:मिनट — 09:30" : "HH:MM — e.g. 09:30"}
            className="cosmic-input" style={{ ...bigInput, flex: 1 }} />
          <select data-testid={`${prefix}-time-period`} value={period} className="cosmic-input"
            onChange={(e) => { setPeriod(e.target.value); push(text, e.target.value); }}
            style={{ ...bigInput, width: "92px", flexShrink: 0 }}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      )}
      <p style={hintStyle}>
        {hi ? "समय याद नहीं? 12:00 रहने दें — बाद में बदल सकते हैं।" : "Don't know the exact time? Leave 12:00 — you can refine it later."}
      </p>
    </div>
  );
};
